import React, { useState, useEffect, useRef } from 'react'
import { withStyles, ClassNameMap } from '@mui/styles'
import stylesheet from '../detail-container.stylesheet'
import {
	Dialog, DialogContent, DialogTitle, DialogActions, Grid, IconButton, Typography, Box, Accordion, AccordionSummary,
	AccordionDetails, Card, CardContent, Stack, Button, MenuItem, Tooltip, Popover, Checkbox, LinearProgress
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import Draggable from "react-draggable"
import { PatientService } from '../../../../services/patient.service'
import EmptyCardComponent from './empty-card.component'
import ContextualHelpDataComponent from './contextual-help-data.component'
import { useSelector } from 'react-redux'
import { RootState } from '../../../../redux/store'
import { toast } from 'react-toastify'
import Helper from '../../../../utils/helper'
import ProviderConfirmationDialogComponent from './provider-confirmation-dialog.component'

interface IProps {
	open: boolean
	input: any
	confirmMedSub: any
	handleClose: any
	classes: ClassNameMap
}

const MedSubstituteComponent: React.FC<IProps> = ({ open, input, confirmMedSub, handleClose, classes }) => {
	const patientDetail = useSelector((state: RootState) => state.patientDetail.patientDetail)
	const [data, setData] = useState({} as any)
	const [confirmMedSubAction, setConfirmMedSubAction] = useState(false)
	const [medDoseInfo, setMedDoseInfo] = useState({} as any)
	const [loading, setLoading] = useState(false)
	const [isSubmit, setIsSubmit] = useState(false)
	const patientService = new PatientService()
	const conextRef = useRef(null) as any
	const [contextData, setContextData] = useState([] as any)
	const [selectedMed, SetSelectedMed] = useState(null as any)
	const [selectedSubMed, SetSelectedSubMed] = useState(null as any)
	const [currMed, setCurrMed] = useState({} as any)
	const [expanded, setExpanded] = useState(null)

	useEffect(() => {
		getMedSubList()
	}, [])

	const handleConfirmActionClose = () => setConfirmMedSubAction(false)

	const handleSubmit = () => currMed?.meets_exclusion ? setConfirmMedSubAction(true) : confirmSubmitMedSub()

	const confirmSubmitMedSub = (isFlag = false, notes = '') => {
		setIsSubmit(true)
		handleConfirmActionClose();
		confirmMedSub(selectedMed, selectedSubMed, notes)
	}

	const getMedSubList = () => {
		setLoading(true)
		input?.id && input?.currentProgram !== undefined &&
			patientService.getMedSubList(patientDetail?.id, input?.id, input.currentProgram).then((res: any) => {
				setData(res?.data || [])
				setLoading(false)
			}).catch((err: any) => {
				setLoading(false)
				setTimeout(() => { handleClose(); }, 500);
				toast.error(Helper.getErrorText(err), Helper.bottom_center())
			})
	}

	const getMoreDetails = (e: any, id: any, type: string) => {
		e.stopPropagation();
		e.preventDefault();
		if (type === 'More information') {
			(contextData.findIndex((i: any) => i === id) === -1)
				? setContextData([...contextData, id])
				: setContextData(contextData.filter((i: any) => i !== id))
		}
	}

	const getTargetSchedule = (title: any, sub_id: string, med_id: string, criObj: any) => {
		return <Accordion square className={classes.zeroTopPadding}
			sx={{ backgroundColor: 'transparent !important' }}
			onClick={(e: any) => getMoreDetails(e, med_id, title)}
		>
			<AccordionSummary id={`med-sub-target-sch}`} className={classes.zeroPadding}
				expandIcon={<ExpandMoreIcon sx={{ fill: '#999999', fontSize: '1rem !important', }} />}
			>
				<Typography className={classes.evalBoxVal}>{title}</Typography>
			</AccordionSummary>
			<AccordionDetails className={classes.zeroPadding}>
				<Box pl={1}>
					{title === 'More information' && contextData.includes(med_id) &&
						<Card variant='outlined' className={classes.medChoiceCardContent} sx={{ minHeight: '4rem' }}>
							<CardContent className={classes.zeroPadding}>
								<ContextualHelpDataComponent
									id={med_id}
									category={'sub_medications'}
									handleLoading={(flag: boolean) => setLoading(false)}
								/>
							</CardContent>
						</Card>
					}
					{title === 'Target schedules' && criObj?.map((i: any) =>
						<Typography className={classes.evalBoxLabel} variant="body2" color="textSecondary">
							{i}
						</Typography>
					)}
				</Box>
			</AccordionDetails>
		</Accordion>
	}

	const getCriteria = (title: any, key: string, info: any) => {
		const criObj = info[key]

		return <Grid container spacing={0.4} direction="row" wrap="nowrap">
			<Grid item>
				<Typography className={classes.evalBoxVal}>{title}</Typography>
			</Grid>
			<Grid item>
				{title === 'Target med'
					? <Stack direction={'row'}>
						<Typography className={classes.evalBoxLabel} variant="body2" color="textSecondary">{criObj}</Typography>
						<Button variant="outlined"
							className={`${classes.medSubSelectBtn}  ${selectedMed === info?.med_id ? classes.medSubSelectActiveBtn : ''}`}
							onClick={() => {
								setCurrMed({ med_id: info?.med_id, sub_id: info?.sub_id, name: info.target_aa_formulation, meets_exclusion: info.meets_exclusion })
								SetSelectedMed(info?.med_id)
								SetSelectedSubMed(info?.sub_id)
							}}>
							{'Select'}
						</Button>
					</Stack>
					: <Typography className={classes.evalBoxLabel} variant="body2"
						color={title === 'Exclusion reason' && info?.exclusion_criteria ? 'red !important' : 'textSecondary'}
					>
						{criObj}
					</Typography>
				}
			</Grid>
		</Grid>
	}

	const getExlusionCriteria = (title: any, criteriaObj: any, criteria: boolean) => {
		let status = 'Pass'
		const cList = criteriaObj && Object.keys(criteriaObj)?.map((m: any, key: any) => {
			const { value, dependents, dependent_has_override } = criteriaObj[m]
			if (value === criteria && status === 'Pass') status = 'Fail'

			const depenList = dependents && Object.keys(dependents)?.map((key: any) => `${key}: ${dependents[key]}`).join(', ');
			return <Grid item key={`${key}-${m}}`}>
				<Stack direction={'row'}>
					<Typography className={classes.evalBoxLabel} variant="body2" color="textSecondary"
						sx={{ backgroundColor: dependent_has_override ? '#ffff00cc !important' : 'inherit' }}>
						{m}
					</Typography>
					<Typography className={classes.evalBoxLabel} variant="body2" color="textSecondary">:&nbsp;</Typography>
					<Typography className={classes.evalBoxLabel} variant="body2"
						color={(value === criteria) ? 'red !important' : 'textSecondary'}
					>
						{JSON.stringify(value)}
					</Typography>
					<Typography className={classes.evalBoxLabel} variant="body2" color="textSecondary">
						&nbsp;{depenList && `[${depenList}]`}
					</Typography>
				</Stack>
			</Grid>
		})

		return <Grid item className={classes.fullWidth}>
			<Grid container direction="column">
				<Grid item>
					<Stack direction={'row'} spacing={0.4}>
						<Typography className={classes.evalBoxVal}>
							{title}
						</Typography>
						{Object.keys(cList)?.length > 0 &&
							<Typography className={classes.evalBoxVal} variant="body2" color={`${status === 'Fail' ? 'red' : 'green'} !important`}> {status} </Typography>
						}
					</Stack>
				</Grid>
				<Grid item>
					<Grid container direction="column" className={classes.evalSubBox}>
						{cList}
					</Grid>
				</Grid>
			</Grid>
		</Grid>
	}

	const getMedDose = (e: any, sub_id: any, med_id: any, expanded: any, currMedId: any) => {
		e.stopPropagation();
		e.preventDefault();
		setExpanded(expanded ? currMedId : null);
		if (expanded) {
			patientService.getMedSubDetails(patientDetail?.id, sub_id, med_id).then((res: any) => {
				(res?.data) && setMedDoseInfo({ ...res?.data, med_id: med_id })
			}).catch((error: any) => {
				toast.error(error?.response?.data?.error?.message || `Failed to get does information`, Helper.bottom_center())
			})
		}
	}

	const getCriteriaList = (title: any, criteriaObj: any, criteria: boolean) => {
		let status = ((title.includes('exclusion') && criteria === true) ||
			(title.includes('inclusion') && criteria === false))
			? 'Fail' : 'Pass'

		if (title.includes('exclusion') && criteria === false)
			status = 'Pass'

		const cmpVal = title.includes('exclusion') ? true : false
		const cList = criteriaObj !== undefined && Object.keys(criteriaObj)?.length > 0 && Object.keys(criteriaObj)?.map((m: any, key: any) => {
			const { value, dependents, dependent_has_override } = criteriaObj[m]
			// if (title.includes('exclusion') && value === criteria && status === 'Pass')
			// 	status = 'Fail'
			if (title.includes('inclusion') && value === criteria && status === 'Fail')
				status = 'Pass'

			const depenList = dependents && Object.keys(dependents)?.map((key: any) => `${key}: ${dependents[key]}`).join(', ');
			return <Grid item key={`${key}-${m}}`}>
				<Stack direction={'row'}>
					<Typography className={classes.evalBoxLabel} variant="body2" color="textSecondary"
						sx={{ backgroundColor: dependent_has_override ? '#ffff00cc !important' : 'inherit' }}>
						{m}
					</Typography>
					<Typography className={classes.evalBoxLabel} variant="body2" color="textSecondary">:&nbsp;</Typography>
					<Typography className={classes.evalBoxLabel} variant="body2"
						color={(value === cmpVal)
							? `${title.includes('exclusion') ? 'red' : 'green'} !important`
							: 'textSecondary'}
					>
						{JSON.stringify(value)}
					</Typography>
					<Typography className={classes.evalBoxLabel} variant="body2" color="textSecondary">
						&nbsp;{depenList && `[${depenList}]`}
					</Typography>
				</Stack>
			</Grid>
		})

		return <Grid item className={classes.fullWidth}>
			<Grid container direction="column">
				<Grid item>
					<Stack direction={'row'} spacing={0.4}>
						<Typography className={classes.evalBoxVal}>
							{title}
						</Typography>
						{Object.keys(cList)?.length > 0 &&
							<Typography className={classes.evalBoxVal} variant="body2" color={`${status === 'Fail' ? 'red' : 'green'} !important`}> {status} </Typography>
						}
					</Stack>
				</Grid>
				<Grid item>
					<Grid container direction="column" className={classes.evalSubBox}>
						{cList}
					</Grid>
				</Grid>
			</Grid>
		</Grid>
	}

	const renderGroup = (medInfo: any, index: any) => {
		return <Box key={`med-sub-${index}`}>
			<Accordion square
				key={`med-sub-${index}`}
				className={classes.zeroTopPadding}
				sx={{ backgroundColor: 'transparent !important' }}
				expanded={expanded === `med-sub-${index}-${medInfo?.target_aa_formulation}`}
				onChange={(e: any, expanded: any) => getMedDose(e, medInfo?.sub_id, medInfo?.med_id, expanded, `med-sub-${index}-${medInfo?.target_aa_formulation}`)}
			>
				<AccordionSummary expandIcon={<ExpandMoreIcon />} className={classes.zeroPadding} id={`med-sub-${index}`}>
					<Typography variant="subtitle2" className={classes.subtitle2}
						color={selectedSubMed === medInfo?.sub_id ? 'var(--color-primary)' : 'inherit'}
					>
						{medInfo?.target_aa_formulation} 	{medDoseInfo?.med_id === medInfo?.med_id &&
							<Typography variant="subtitle2" component={'span'} color='GrayText'>
								<small>({typeof medDoseInfo?.dose === 'object' ? medDoseInfo?.dose?.join('/') : medDoseInfo?.dose?.toString()}
									{' '}{medDoseInfo?.unit || ''})
								</small>
							</Typography>
						}
					</Typography>
				</AccordionSummary>
				<AccordionDetails className={classes.zeroPadding}>
					<Box sx={{ paddingLeft: '0.8rem' }} className={classes.fullWidth}>
						<Grid container spacing={0.4} direction="column" sx={{ paddingRight: '1rem' }}>
							<Grid item className={classes.fullWidth}>
								{getCriteria(`Target med`, 'target_aa_formulation', medInfo)}
							</Grid>
							<Grid item>
								{getCriteria(`Substitution`, 'substitution_type', medInfo)}
							</Grid>
							<Grid item>
								{getCriteria(`Indication`, 'indication_name', medInfo)}
							</Grid>
							<Grid item>
								{getCriteriaList(`Meets substitution inclusion`, medInfo?.sub_incl_criteria, medInfo?.meets_sub_inclusion_criteria || false)}
							</Grid>
							<Grid item>
								{getCriteriaList(`Meets substitution exclusion`, medInfo?.sub_exclusion_criteria, medInfo?.meets_sub_exclusion_criteria)}
							</Grid>
							<Grid item>
								{getCriteriaList(`Meets exclusion`, medInfo?.exclusion_criteria, (medInfo?.meets_exclusion || Boolean(medInfo?.exclusion_reason)))}
							</Grid>
							<Grid item>
								{getCriteria(`Exclusion reason`, 'exclusion_reason', medInfo)}
							</Grid>
							<Grid item>
								<Box sx={{ marginLeft: '-0.35rem !important', height: 'inherit' }}>
									{getTargetSchedule(`Target schedules`, medInfo?.sub_id, medInfo?.med_id, medInfo?.target_schedules)}
								</Box>
							</Grid>
							<Grid item>
								<Box sx={{ marginLeft: '-0.35rem !important', height: 'inherit' }}>
									{getTargetSchedule(`More information`, medInfo?.sub_id, medInfo?.med_id, {})}
								</Box>
							</Grid>
						</Grid>
					</Box>
				</AccordionDetails>
			</Accordion>
		</Box>
	}

	const renderMedGroup = (title: any, item: any) => {
		return <Box key={`med-${title}`}>
			<Accordion square className={classes.zeroTopPadding} sx={{ backgroundColor: 'transparent !important' }}>
				<AccordionSummary expandIcon={<ExpandMoreIcon />} className={classes.zeroPadding} id={`med-${title}`}>
					<Typography variant="subtitle2" className={classes.subtitle2}
						color={item?.some((i: any) => i?.sub_id === selectedSubMed) ? 'var(--color-primary)' : 'inherit'}
					>
						{title}
					</Typography>
				</AccordionSummary>
				<AccordionDetails className={classes.zeroPadding}>
					{item && item
						?.sort((a: any, b: any) => a.target_aa_formulation > b.target_aa_formulation ? 1 : -1)
						?.map((currItem: any, indx: any) => renderGroup(currItem, indx))
					}
				</AccordionDetails>
			</Accordion>
		</Box>
	}

	const renderMedSubList = () => {
		return <Box className={classes.medChoicesRecomBox} sx={{ paddingBottom: '1rem', position: 'relative' }}>
			{Object.keys(data?.substitution_list || {})?.length > 0
				? Object.keys(data?.substitution_list)?.map((currItem: any) => renderMedGroup(currItem, data?.substitution_list[currItem]))
				: <Box pt={2}><EmptyCardComponent msg={`No substitute medication available.`} /></Box>
			}
		</Box>
	}

	return <>
		{open &&
			<Draggable ref={conextRef} defaultPosition={{ x: 80, y: 20 }} handle='.med-sub-dialog'>
				<Dialog
					id={`med-sub-id-${input?.id}`}
					fullWidth={true}
					maxWidth="md"
					open={open}
					onClose={handleClose}
					hideBackdrop
					disableScrollLock
					sx={{
						position: "absolute",
						left: '10rem',
						top: '1rem'
					}}
				>
					<DialogTitle className={classes.contextualHelpHeader} classes={{ root: 'med-sub-dialog' }}>
						<Grid container justifyContent="space-between">
							<Grid item>
								<Typography color="textSecondary">
									<small>{`${input?.category} / Medication Substitution`}</small>
								</Typography>
								<Typography id="modal-modal-title" variant="h6">
									<strong>{`${input?.title || 'Medication Substitution'}`}</strong>
								</Typography>
							</Grid>
							<Grid item>
								<IconButton size="small" onClick={() => handleClose()}><CloseOutlinedIcon /></IconButton>
							</Grid>
						</Grid>
					</DialogTitle>
					<DialogContent className={`${classes.contextualHelpContext} ${classes.zeroPadding}`}>
						{loading ? <LinearProgress /> : renderMedSubList()}
					</DialogContent>
					<Box className={classes.boxBorder} />
					<DialogActions className={classes.zeroPadding}>
						<Grid container spacing={2} justifyContent="right" className={classes.containerPadding}>
							<Grid item>
								<Button variant="outlined" size="small"
									disabled={!selectedMed || confirmMedSubAction || loading || isSubmit}
									onClick={() => handleSubmit()}>
									{'Submit'}
								</Button>
							</Grid>
							<Grid item>
								<Button variant="outlined" size="small" onClick={() => handleClose()}>
									{'Cancel'}
								</Button>
							</Grid>
						</Grid>
					</DialogActions>
				</Dialog>
			</Draggable>
		}
		{confirmMedSubAction && (
			<ProviderConfirmationDialogComponent
				open={confirmMedSubAction}
				content={`${currMed?.name} meets exclusion criteria. Are you sure you want to proceed?`}
				handleConfirmClose={handleConfirmActionClose}
				confirmStatus={confirmSubmitMedSub}
			/>
		)}
	</>
}

export default withStyles(stylesheet)(MedSubstituteComponent)